/**
 * FolderBar — breadcrumb path + the current folder's child folders (as cards)
 * + an inline "New folder" affordance. Mirrors a file-manager's folder strip.
 */
import * as React from "react";
import { FolderOpen, Folder, ChevronRight, FolderPlus, X } from "lucide-react";

export interface FolderNode {
  id: string;
  parentId: string | null;
  name: string;
}

export interface FolderBarProps {
  folders: FolderNode[];
  currentFolderId: string | null;
  onNavigate: (folderId: string | null) => void;
  onCreate: (name: string, parentId: string | null) => void;
  creating: boolean;
}

export function FolderBar({ folders, currentFolderId, onNavigate, onCreate, creating }: FolderBarProps) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");

  const children = folders.filter((f) => f.parentId === currentFolderId);
  const breadcrumb = buildBreadcrumb(folders, currentFolderId);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate(name.trim(), currentFolderId);
      setName("");
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-2.5">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-1.5 text-sm">
        <button
          type="button"
          onClick={() => onNavigate(null)}
          className="inline-flex items-center gap-1.5 font-medium text-ink hover:text-sky-700"
        >
          <FolderOpen size={15} aria-hidden /> All documents
        </button>
        {breadcrumb.map((f) => (
          <React.Fragment key={f.id}>
            <ChevronRight size={13} className="text-line-2" aria-hidden />
            <button type="button" onClick={() => onNavigate(f.id)} className="text-ink-2 hover:text-sky-700">
              {f.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Child folders + new-folder */}
      <div className="flex flex-wrap items-center gap-2">
        {children.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => onNavigate(f.id)}
            className="group inline-flex items-center gap-2 rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink-2 shadow-sm transition-colors hover:border-line-2 hover:bg-bg"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-sky-50 text-sky-700">
              <Folder size={13} aria-hidden />
            </span>
            <span className="font-medium">{f.name}</span>
          </button>
        ))}

        {open ? (
          <form onSubmit={submit} className="inline-flex items-center gap-1.5">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Folder name"
              className="h-9 w-44 rounded-sm border border-line bg-bg px-2.5 text-sm focus:border-sky-600 focus:bg-paper focus:outline-none focus:ring-2 focus:ring-sky-100"
            />
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="h-9 rounded-sm bg-ink px-3 text-sm font-semibold text-paper hover:bg-ink-2 disabled:opacity-60"
            >
              Create
            </button>
            <button type="button" onClick={() => setOpen(false)} className="p-1.5 text-muted hover:text-ink">
              <X size={15} aria-hidden />
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-line bg-transparent px-3 py-2 text-sm text-muted hover:border-sky-300 hover:text-sky-700"
          >
            <FolderPlus size={14} aria-hidden /> New folder
          </button>
        )}
      </div>
    </div>
  );
}

/** Walk the parent chain to render the path from root to the current folder. */
function buildBreadcrumb(folders: FolderNode[], folderId: string | null): FolderNode[] {
  if (folderId === null) return [];
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: FolderNode[] = [];
  let cursor: string | null = folderId;
  let guard = 0;
  while (cursor && guard < 10) {
    const f = byId.get(cursor);
    if (!f) break;
    path.unshift(f);
    cursor = f.parentId;
    guard += 1;
  }
  return path;
}
